import {
  FormSidebar,
  FormSidebarActions,
  FormSidebarCallouts,
} from "../layouts/FormSidebar";
import ButtonSave from "../form/ButtonSave";
import { FormikProps } from "formik";
import {
  TilsynserklaeringFormValues,
  TilsynserklaeringMetadata,
} from "../../interfaces/tilsynserklaering";
import { putTilsynsErklaering } from "../../api/tilsynserklaering";
import { TILSYN_FORM_INITIAL_VALUES } from "../../constants/tilsyn/initialValues";
import { TE_PREFILL } from "../../constants/klientkontoerklaering/prefill";
import Modal, { ModalProvider, ModalTrigger } from "../Modal/Modal";
import ButtonPreview from "../form/ButtonPreview";
import ButtonPrint from "../form/ButtonPrint";
import HtmlResult, { getHtmlResult } from "./HtmlResult";
import { useRevalidator, useRouteLoaderData } from "react-router";
import {
  tilsynSection1CompleteHelper,
  tilsynSection2CompleteHelper,
  tilsynSection3CompleteHelper,
  tilsynSection4CompleteHelper,
} from "../../util/tilsynserklaering/isComplete";
import { TETexts } from "./texts";
import { replacer } from "../../util/json";
import { TE_FORM_VERSION } from "../../constants/config";
import UnderskrivModal from "../Modal/UnderskrivModal";
import { format } from "date-fns";
import { ROUTES } from "../../constants/routes";
import { HomeLoaderResponse } from "../../interfaces/loaders";

const TESidebar = ({
  formik: { values, errors, touched, dirty, isValid },
  id,
  metaData,
}: {
  id: string;
  formik: FormikProps<TilsynserklaeringFormValues>;
  texts: TETexts;
  metaData: TilsynserklaeringMetadata;
}) => {
  const revalidator = useRevalidator();
  const demoMode = import.meta.env.VITE_IS_DEMO?.toLocaleLowerCase() === "true";

  const data = useRouteLoaderData(ROUTES.homeIndex.id) as
    HomeLoaderResponse | undefined;

  const userInfo = data?.userInfo;

  const { section1IsStarted, section1IsComplete } =
    tilsynSection1CompleteHelper(values, errors, touched);
  const { section2IsStarted, section2IsComplete } =
    tilsynSection2CompleteHelper(values, errors, touched);
  const { section3IsStarted, section3IsComplete } =
    tilsynSection3CompleteHelper(values, errors, touched);
  const { section4IsStarted, section4IsComplete } =
    tilsynSection4CompleteHelper(values, errors, touched);

  return (
    <FormSidebar stickyTop="5rem">
      <FormSidebarCallouts>
        <p className="text-muted fs-6">
          Senest gemt d.{" "}
          {metaData.senestGemt &&
            format(new Date(metaData.senestGemt), "d.M.yyyy' kl. 'HH:mm")}
        </p>
      </FormSidebarCallouts>

      {section1IsStarted && !section1IsComplete && (
        <div className="callout callout-danger mb-2 fs-6">
          Sektion 1 påbegyndt, men ikke færdig.
        </div>
      )}

      {section2IsStarted && !section2IsComplete && (
        <div className="callout callout-danger mb-2 fs-6">
          Sektion 2 påbegyndt, men ikke færdig.
        </div>
      )}
      {section3IsStarted && !section3IsComplete && (
        <div className="callout callout-danger mb-2 fs-6">
          Sektion 3 påbegyndt, men ikke færdig.
        </div>
      )}
      {section4IsStarted && !section4IsComplete && (
        <div className="callout callout-danger mb-2 fs-6">
          Sektion 4 påbegyndt, men ikke færdig.
        </div>
      )}

      {isValid ? (
        <div className="callout callout-success fs-6">
          Tilsynserklæring kan godkendes{" "}
          {userInfo?.identityType === "Accountant" && "af advokat"}
        </div>
      ) : (
        <div className="callout fs-6">Tilsynserklæring skal udfyldes</div>
      )}
      <FormSidebarActions>
        <div className="d-flex gap-3">
          <ButtonSave
            disabled={!dirty}
            onClick={async () => {
              const jsonData = {
                form: values,
                version: TE_FORM_VERSION,
              };
              await putTilsynsErklaering({
                id,
                jsonData: JSON.stringify(jsonData, replacer),
                formular: getHtmlResult(values, metaData),
                status: "Kladde",
                signeretAf: "",
              });
              if (revalidator.state === "idle") {
                revalidator.revalidate();
              }
            }}
          />
          <ModalProvider>
            <ModalTrigger asChild>
              <ButtonPreview />
            </ModalTrigger>
            <Modal
              title={
                <div className="d-flex w-100 align-items-center justify-content-between">
                  <span>Forhåndsvisning</span>
                  <ButtonPrint />
                </div>
              }
            >
              <HtmlResult values={values} metadata={metaData} />
            </Modal>
          </ModalProvider>
        </div>
        <ModalProvider>
          <ModalTrigger asChild>
            <button
              className="btn btn-lg btn-primary w-100"
              type="button"
              disabled={!isValid || userInfo?.identityType === "Accountant"}
              onClick={() => {}}
            >
              Godkend med MitID <br /> og indsend
            </button>
          </ModalTrigger>
          <UnderskrivModal
            numEmails={2}
            isFinal
            onSubmitHandler={async ({ email, email2 }) => {
              try {
                const kvittering_emailadresse: {
                  advokat: string;
                  ekstra_email?: string;
                } = { advokat: email };
                if (email2) {
                  kvittering_emailadresse.ekstra_email = email2;
                }
                const jsonData = {
                  form: values,
                  version: TE_FORM_VERSION,
                  kvittering_emailadresse,
                };
                await putTilsynsErklaering({
                  id,
                  jsonData: JSON.stringify(jsonData, replacer),
                  formular: getHtmlResult(values, metaData),
                  status: "Kladde",
                  signeretAf: "",
                });
                if (revalidator.state === "idle") {
                  revalidator.revalidate();
                }
                window.location.href = `${
                  import.meta.env.VITE_BACKEND_ENDPOINT
                }/Signering/Signer?erklaeringId=${id}&signeringstype=Tilsynserklaering`;
              } catch (error) {
                console.error(error);
              }
            }}
          />
        </ModalProvider>
        {demoMode && (
          <>
            <button
              type="button"
              className="mt-4 btn btn-secondary border"
              onClick={async () => {
                await putTilsynsErklaering({
                  id,
                  jsonData: JSON.stringify(TE_PREFILL, replacer),
                  formular: getHtmlResult(TILSYN_FORM_INITIAL_VALUES, metaData),
                  signeretAf: "",
                  signeret: false,
                });
                if (revalidator.state === "idle") {
                  revalidator.revalidate();
                }
              }}
            >
              Prefill form
            </button>
            <button
              type="button"
              className="btn btn-secondary border"
              onClick={async () => {
                await putTilsynsErklaering({
                  id: id,
                  jsonData: JSON.stringify(
                    TILSYN_FORM_INITIAL_VALUES,
                    replacer,
                  ),
                  formular: getHtmlResult(TILSYN_FORM_INITIAL_VALUES, metaData),
                  signeretAf: "",
                  signeret: false,
                });
                if (revalidator.state === "idle") {
                  revalidator.revalidate();
                }
              }}
            >
              Clear form
            </button>
          </>
        )}
      </FormSidebarActions>
    </FormSidebar>
  );
};

export default TESidebar;
